import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../services/redux/store";
import postsServices from "../../../services/api/service/postsService";
import {
  setActiveHorizontalIndex,
  setHorizontalSteps,
  setActiveVerticalIndex,
  updateFormData,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import dropdownService from "../../../services/api/service/dropdownService";
import ModalPopup from "../../ui/ModalPopup";
import { PrimaryButton } from "../../ui/Button";
import eligibilityCheckServices from "../../../services/api/service/losService";
import { numberToWord } from "../../../utils/helpers";

// ─── Field name helpers ──────────────────────────────────────────────────────
// Collateral fields use prefix: collateral<Suffix>
// e.g. collateralType, collateralValue, collateralDescription, collateralOwner

interface CollateralDetailsProps {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  activeCollateralNumber?: number;
  onActiveCollateralChange?: (collateralNumber: number) => void;
  onCollateralOptionsChange?: (options: {
    addressTypes: any[];
    propertyTypes: any[];
    propertySubTypes: any[];
  }) => void;
}

const CollateralDetails: React.FC<CollateralDetailsProps> = ({
  fields,
  renderField,
  formikRef,
  values,
  activeCollateralNumber,
  onActiveCollateralChange,
  onCollateralOptionsChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const formValues = values || formikRef?.current?.values || {};
  const { trackingId, horizontalSteps, activeHorizontalIndex, activeVerticalIndex, fetchUserApplicationData, formData } =
    useSelector((state: RootState) => state.stepper);
  const firstCollateralFieldsRef = useRef<any[]>([]);

  const getNormalized = (name: any) =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const mergeFieldsByName = (currentFields: any[], newFields: any[]) => {
    const names = new Set(currentFields.map((field: any) => getNormalized(field?.name)));
    return [
      ...currentFields,
      ...newFields.filter((field: any) => {
        const name = getNormalized(field?.name);
        if (names.has(name)) return false;
        names.add(name);
        return true;
      }),
    ];
  };

  const hasCollateralField = useMemo(() => {
    if (!Array.isArray(fields)) return null;
    return fields.find((f: any) => getNormalized(f?.name) === "hascollateral") || null;
  }, [fields]);

  const collateralFields = useMemo(() => {
    if (!Array.isArray(fields)) return [];
    const currentFields = fields
      .map((f: any, idx: number) => ({ f, idx }))
      .filter(({ f }: any) => {
        const normalized = getNormalized(f?.name);
        return (
          (normalized.startsWith("collateral") && normalized !== "hascollateral") ||
          normalized.startsWith("coownercollateral")
        );
      })
      .sort((a: any, b: any) => {
        const aOrder =
          typeof a?.f?.fieldOrder === "number"
            ? a.f.fieldOrder
            : Number.MAX_SAFE_INTEGER;
        const bOrder =
          typeof b?.f?.fieldOrder === "number"
            ? b.f.fieldOrder
            : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.idx - b.idx;
      })
	      .map(({ f }: any) => f);

    return currentFields.length > 0 ? currentFields : firstCollateralFieldsRef.current;
	  }, [fields]);

  const getSimpleValue = (value: any) =>
    String(
      value && typeof value === "object"
        ? value?.value ?? value?.label ?? ""
        : value ?? "",
    ).trim();

  const normalizeHasCollateralValue = (value: any) =>
    getSimpleValue(value).toLowerCase();

  const suppressHasCollateralNoModalRef = useRef(false);

  const setHasCollateralValue = (value: "Yes" | "No", suppressNoModal = false) => {
    if (!hasCollateralField?.name) return;
    if (value === "No" && suppressNoModal) {
      suppressHasCollateralNoModalRef.current = true;
    }
    formikRef?.current?.setFieldValue(hasCollateralField.name, value, false);
    dispatch(updateFormData({ [hasCollateralField.name]: value }));
  };

  const applicationFetchCollateralState = useMemo(() => {
    let hasCollateralStep = false;
    let hasCollateralNo = false;
    const nums = new Set<number>();

    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== "object") return;

      const stepName = String(node?.verticalStepName || node?.stepLabel || "").trim();
      if (Number(node?.verticalStepId ?? node?.id) === 14 || stepName === "Collateral Details") {
        hasCollateralStep = true;
      }

      Object.entries(node).forEach(([key, value]) => {
        const normalizedKey = getNormalized(key);
        const match = normalizedKey.match(/^(?:collateral|coownercollateral)(\d+)/);
        if (match && normalizedKey !== "hascollateral" && getSimpleValue(value)) {
          nums.add(Number(match[1]));
        }
        if (normalizedKey === "hascollateral" && normalizeHasCollateralValue(value) === "no") {
          hasCollateralNo = true;
        }
        visit(value);
      });
    };

    visit(fetchUserApplicationData);
    return {
      hasCollateralStep,
      hasCollateralNo,
      numbers: Array.from(nums).sort((a, b) => a - b),
    };
  }, [fetchUserApplicationData]);

  const isCollateralNoFromApplicationFetch = useMemo(() => {
    if (!fetchUserApplicationData) return false;
    if (!applicationFetchCollateralState.hasCollateralStep) return true;
    return applicationFetchCollateralState.hasCollateralNo;
  }, [applicationFetchCollateralState, fetchUserApplicationData]);

  const applicationFetchCollateralNumbers = applicationFetchCollateralState.numbers;

  const savedValueCollateralNumbers = useMemo(() => {
    const nums = new Set<number>();
    [{ ...(formData || {}) }, { ...(formValues || {}) }].forEach((source) => {
      Object.entries(source).forEach(([key, value]) => {
        const normalizedKey = getNormalized(key);
        const match = normalizedKey.match(/^(?:collateral|coownercollateral)(\d+)/);
        if (match && normalizedKey !== "hascollateral" && getSimpleValue(value)) {
          nums.add(Number(match[1]));
        }
      });
    });
    return Array.from(nums).sort((a, b) => a - b);
  }, [formData, formValues]);

  const getCollateralNumber = (name: any): number => {
    const normalized = getNormalized(name);
    const match = normalized.match(/^(?:collateral|coownercollateral)(\d+)/);
    return match ? Number(match[1]) : 1;
  };

  const collateralNumbers = useMemo(() => {
    const nums = new Set<number>();
    collateralFields.forEach((f: any) => {
      const normalized = getNormalized(f?.name);
      // Only count base collateral fields (not co-owner) to determine section numbers.
      // Co-owner fields use names like coOwnerCollateral1_2Name where the owner index
      // is appended to the collateral number, which would produce false collateral numbers.
      if (!normalized.startsWith("coownercollateral")) {
        nums.add(getCollateralNumber(f?.name));
      }
    });
    if (!nums.size && collateralFields.length > 0) nums.add(1);
    return Array.from(nums).sort((a, b) => a - b);
  }, [collateralFields]);

  const hasCollateralValue = hasCollateralField?.name
    ? normalizeHasCollateralValue(formValues?.[hasCollateralField.name])
    : "";
    console.log(hasCollateralValue, 'hasCollateralValue')
  const isHasCollateralNo = hasCollateralValue === "no";
  const isHasCollateralYes = hasCollateralValue === "yes";
  console.log(isHasCollateralNo, isHasCollateralYes, 'isHasCollateralNo, isHasCollateralYes')
  console.log(hasCollateralValue, 'isHasCollateralNo, isHasCollateralYes')
  const [fetchedCollateralNumbers, setFetchedCollateralNumbers] = useState<number[]>([]);
  const [fetchedCoOwnerSections, setFetchedCoOwnerSections] = useState<Record<number, number[]>>({});
  const [locallyAddedCollateralNumbers, setLocallyAddedCollateralNumbers] = useState<number[]>([]);
  const [removedCollateralNumbers, setRemovedCollateralNumbers] = useState<number[]>([]);
  const isAddingCollateralRef = useRef(false);
  const previousHasCollateralValueRef = useRef(hasCollateralValue);
  const [isCollateralFetchResolved, setIsCollateralFetchResolved] = useState(false);
  const isCollateralFetchLoading = Boolean(hasCollateralField && trackingId && !isCollateralFetchResolved);

  const availableGetDataCollateralNumbers = useMemo(
    () => collateralNumbers.filter((number) => !removedCollateralNumbers.includes(number)),
    [collateralNumbers, removedCollateralNumbers],
  );

  const displayedCollateralNumbers = useMemo(() => {
    if (hasCollateralField && trackingId && !isCollateralFetchResolved) return [];
    if (hasCollateralField && isHasCollateralNo) return [];
    if (
      hasCollateralField &&
      trackingId &&
      isCollateralFetchResolved &&
      !hasCollateralValue &&
      fetchedCollateralNumbers.length === 0
    ) {
      return [];
    }
    const persistedCollateralNumbers =
      applicationFetchCollateralNumbers.length > 0
        ? applicationFetchCollateralNumbers
        : fetchedCollateralNumbers.length > 0
          ? fetchedCollateralNumbers
          : savedValueCollateralNumbers;
    if (persistedCollateralNumbers.length > 0) {
      const allowedNumbers = new Set([
        ...persistedCollateralNumbers,
        ...locallyAddedCollateralNumbers,
      ]);
      return availableGetDataCollateralNumbers.filter((number) => allowedNumbers.has(number));
    }
    if (availableGetDataCollateralNumbers.length > 0) {
      return availableGetDataCollateralNumbers.includes(1)
        ? [1]
        : [availableGetDataCollateralNumbers[0]];
    }
    if (!hasCollateralField || isHasCollateralYes) return [1];
    return [];
  }, [
    availableGetDataCollateralNumbers,
    applicationFetchCollateralNumbers,
    fetchedCollateralNumbers,
    hasCollateralField,
    hasCollateralValue,
    isCollateralFetchResolved,
    isHasCollateralNo,
    isHasCollateralYes,
    locallyAddedCollateralNumbers,
    savedValueCollateralNumbers,
    trackingId,
  ]);

  const [expanded, setExpanded] = useState<number | false>(
    activeCollateralNumber ?? displayedCollateralNumbers[0] ?? 1,
  );
  useEffect(() => {
    if (typeof activeCollateralNumber === "number") {
      setExpanded(activeCollateralNumber);
    }
  }, [activeCollateralNumber]);
  const [visibleCoOwnerCount, setVisibleCoOwnerCount] = useState<Record<number, number>>({});
  const [isAddingCoOwner, setIsAddingCoOwner] = useState<Record<number, boolean>>({});
  const [isRemovingCoOwner, setIsRemovingCoOwner] = useState<Record<string, boolean>>({});
  const [pendingCoOwnerRemove, setPendingCoOwnerRemove] = useState<{
    collateralNumber: number;
    sectionFields: any[];
    totalSections: number;
    sectionIdx: number;
  } | null>(null);
  const [pendingCollateralRemove, setPendingCollateralRemove] = useState<number | null>(null);
  const [isAddingCollateral, setIsAddingCollateral] = useState(false);
  const [isRemovingCollateral, setIsRemovingCollateral] = useState<Record<number, boolean>>({});
  const [addressTypeOptions, setAddressTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
	  const [propertyTypeOptions, setPropertyTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
		  const [propertySubTypeOptions, setPropertySubTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
			  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("");
			  const [fetchedCollateralTypeMap, setFetchedCollateralTypeMap] = useState<Record<number, boolean>>({});
	  const [pendingHasCollateralNo, setPendingHasCollateralNo] = useState(false);

  const hasFetchedValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "object") {
      const optionValue = value?.value ?? value?.label;
      return String(optionValue ?? "").trim() !== "";
    }
    return String(value).trim() !== "";
  };

  const getCollateralNumberFromSequence = (sequenceType: any) => {
    const suffix = String(sequenceType || "").replace(/^COLLATERAL_/i, "").toUpperCase();
    const matched = Object.entries(numberToWord).find(([, word]) => word === suffix);
    return matched ? Number(matched[0]) : null;
  };

	  const collectFetchedCollateralTypes = (data: any) => {
    const locked: Record<number, boolean> = {};
    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== "object") return;

      const sequenceNumber = getCollateralNumberFromSequence(node?.collateralSequenceType);
      if (sequenceNumber && hasFetchedValue(node?.collateralType)) {
        locked[sequenceNumber] = true;
      }

      Object.values(node).forEach(visit);
    };

	    visit(data);
	    return locked;
	  };

  const collectFetchedCollateralNumbers = (data: any) => {
    const nums = new Set<number>();
    const hasRealFetchedCollateralData = (node: any): boolean => {
      if (!node) return false;
      if (Array.isArray(node)) return node.some(hasRealFetchedCollateralData);
      if (typeof node !== "object") return hasFetchedValue(node);

      const fieldName = node?.name || node?.key || node?.fieldName || node?.fieldKey;
      const fieldValue =
        node?.value ?? node?.fieldValue ?? node?.savedValue ?? node?.answer ?? node?.selectedValue;
      if (
        getNormalized(fieldName).match(/^collateral\d*(type|value)$/) &&
        hasFetchedValue(fieldValue)
      ) {
        return true;
      }

      return Object.entries(node).some(([key, value]) => {
        const normalizedKey = getNormalized(key);
        if (
          /^collateral\d*(type|value)$/.test(normalizedKey) &&
          hasFetchedValue(value)
        ) {
          return true;
        }
        return hasRealFetchedCollateralData(value);
      });
    };

    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== "object") return;

      const sequenceNumber = getCollateralNumberFromSequence(node?.collateralSequenceType);
      if (sequenceNumber && hasRealFetchedCollateralData(node)) nums.add(sequenceNumber);

      Object.entries(node).forEach(([key, value]) => {
        const match = getNormalized(key).match(/^collateral(\d+)$/);
        if (match && hasRealFetchedCollateralData(value)) nums.add(Number(match[1]));
        const fieldName = (value as any)?.name || (value as any)?.key || (value as any)?.fieldName || (value as any)?.fieldKey;
        const fieldMatch = getNormalized(fieldName).match(/^collateral(\d*)type$/);
        if (fieldMatch && hasFetchedValue((value as any)?.value ?? (value as any)?.fieldValue)) {
          nums.add(Number(fieldMatch[1] || 1));
        }
        visit(value);
      });
    };

    visit(data);
    return Array.from(nums).sort((a, b) => a - b);
  };

  const collectFetchedCoOwnerSections = (data: any) => {
    const sections: Record<number, Set<number>> = {};
    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== "object") return;

      const fieldKey = node?.fieldKey || node?.fieldName || node?.name || node?.key;
      const fieldMatch = String(fieldKey || "").match(
        /^coOwnerCollateral(\d+)(?:_(\d+))?/i,
      );
      if (fieldMatch && hasFetchedValue(node?.fieldValue ?? node?.value)) {
        const collateralNumber = Number(fieldMatch[1]);
        const ownerNumber = Number(fieldMatch[2] || 1);
        sections[collateralNumber] ??= new Set<number>();
        sections[collateralNumber].add(ownerNumber);
      }

      Object.entries(node).forEach(([key, value]) => {
        const match = String(key).match(/^coOwnerCollateral(\d+)(?:_(\d+))?/i);
        if (match && hasFetchedValue(value)) {
          const collateralNumber = Number(match[1]);
          const ownerNumber = Number(match[2] || 1);
          sections[collateralNumber] ??= new Set<number>();
          sections[collateralNumber].add(ownerNumber);
        }
        visit(value);
      });
    };

    visit(data);
    return Object.fromEntries(
      Object.entries(sections).map(([number, owners]) => [number, Array.from(owners)]),
    ) as Record<number, number[]>;
  };

  const applicationFetchedCoOwnerSections = useMemo(
    () => collectFetchedCoOwnerSections(fetchUserApplicationData),
    [fetchUserApplicationData],
  );

  // Saved (fetched) Ownership Percentage per co-owner field name, e.g.
  // { coOwnerCollateral1_2OwnershipPercentage: "18" } — the baseline used to
  // detect which saved co-owner's percentage the user is editing.
  const collectFetchedCoOwnerPercentages = (data: any) => {
    const percentages: Record<string, string> = {};
    const isPercentageKey = (key: any) =>
      /^coOwnerCollateral\d+(?:_\d+)?OwnershipPercentage$/i.test(String(key || ""));
    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== "object") return;

      const fieldKey = node?.fieldKey || node?.fieldName || node?.name || node?.key;
      const fieldValue = node?.fieldValue ?? node?.value;
      if (isPercentageKey(fieldKey) && hasFetchedValue(fieldValue)) {
        percentages[String(fieldKey)] = getSimpleValue(fieldValue);
      }

      Object.entries(node).forEach(([key, value]) => {
        if (isPercentageKey(key) && hasFetchedValue(value)) {
          percentages[key] = getSimpleValue(value);
        }
        visit(value);
      });
    };

    visit(data);
    return percentages;
  };

  const applicationFetchedCoOwnerPercentages = useMemo(
    () => collectFetchedCoOwnerPercentages(fetchUserApplicationData),
    [fetchUserApplicationData],
  );
  const [fetchedCoOwnerPercentages, setFetchedCoOwnerPercentages] = useState<Record<string, string>>({});

  // ── Fetch Address Types, Property Types, Property Sub Types ───────────────
  useEffect(() => {
    let isMounted = true;
    dropdownService.getAddressTypes().then((data) => {
      if (isMounted && data.length > 0) setAddressTypeOptions(data);
    }).catch(() => {});
    dropdownService.getPropertyTypes().then((data) => {
      if (isMounted && data.length > 0) setPropertyTypeOptions(data);
    }).catch(() => {});
    dropdownService.getPropertySubTypes().then((data) => {
      if (isMounted && data.length > 0) setPropertySubTypeOptions(data);
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
      if (isCollateralNoFromApplicationFetch) {
        setFetchedCollateralTypeMap({});
        setFetchedCollateralNumbers([]);
        setFetchedCoOwnerSections({});
        setFetchedCoOwnerPercentages({});
        setLocallyAddedCollateralNumbers([]);
        setHasCollateralValue("No", true);
        setIsCollateralFetchResolved(true);
        return () => { isMounted = false; };
      }
	    if (!trackingId) {
      setFetchedCollateralTypeMap({});
      setFetchedCollateralNumbers([]);
      setFetchedCoOwnerSections({});
      setFetchedCoOwnerPercentages({});
      setLocallyAddedCollateralNumbers([]);
      setHasCollateralValue("No", true);
      setIsCollateralFetchResolved(true);
	      return () => { isMounted = false; };
	    }
    setIsCollateralFetchResolved(false);
	
	    eligibilityCheckServices.fetchCollateral(trackingId)
	      .then((resp: any) => {
	        if (isMounted) {
          const fetchedData = resp?.data?.data;
          const fetchedNumbers = collectFetchedCollateralNumbers(fetchedData);
          setFetchedCoOwnerSections(collectFetchedCoOwnerSections(fetchedData));
          setFetchedCoOwnerPercentages(collectFetchedCoOwnerPercentages(fetchedData));
	          setFetchedCollateralTypeMap(collectFetchedCollateralTypes(fetchedData));
	          setFetchedCollateralNumbers(fetchedNumbers);
	          if (hasCollateralField?.name) {
	            if (fetchedNumbers.length === 0) {
                setLocallyAddedCollateralNumbers([]);
	              setHasCollateralValue("No", true);
	            } else if (
	              fetchedNumbers.length > 0 &&
              !normalizeHasCollateralValue(formikRef?.current?.values?.[hasCollateralField.name])
            ) {
              setHasCollateralValue("Yes");
            }
          }
          setIsCollateralFetchResolved(true);
	        }
	      })
	      .catch(() => {
	        if (isMounted) {
          setFetchedCollateralTypeMap({});
          setFetchedCollateralNumbers([]);
          setFetchedCoOwnerSections({});
          setFetchedCoOwnerPercentages({});
          setLocallyAddedCollateralNumbers([]);
          setHasCollateralValue("No", true);
          setIsCollateralFetchResolved(true);
        }
	      });
	
	    const refreshFetchedCollateralTypes = () => {
	      eligibilityCheckServices.fetchCollateral(trackingId)
	        .then((resp: any) => {
	          if (isMounted) {
              const fetchedData = resp?.data?.data;
              const fetchedNumbers = collectFetchedCollateralNumbers(fetchedData);
              setFetchedCoOwnerSections(collectFetchedCoOwnerSections(fetchedData));
              setFetchedCoOwnerPercentages(collectFetchedCoOwnerPercentages(fetchedData));
	            setFetchedCollateralTypeMap(collectFetchedCollateralTypes(fetchedData));
              setFetchedCollateralNumbers(fetchedNumbers);
              if (hasCollateralField?.name) {
                if (fetchedNumbers.length === 0) {
                  setLocallyAddedCollateralNumbers([]);
                  setHasCollateralValue("No", true);
                } else if (
                  !normalizeHasCollateralValue(formikRef?.current?.values?.[hasCollateralField.name])
                ) {
                  setHasCollateralValue("Yes");
                }
              }
	          }
	        })
	        .catch(() => {
            if (isMounted) {
              setFetchedCollateralTypeMap({});
              setFetchedCollateralNumbers([]);
              setFetchedCoOwnerSections({});
              setFetchedCoOwnerPercentages({});
              setLocallyAddedCollateralNumbers([]);
              setHasCollateralValue("No", true);
            }
          });
	    };

    window.addEventListener("collateral:saved", refreshFetchedCollateralTypes);

    return () => {
      isMounted = false;
      window.removeEventListener("collateral:saved", refreshFetchedCollateralTypes);
    };
	  }, [trackingId, hasCollateralField?.name, isCollateralNoFromApplicationFetch]);

  useEffect(() => {
	    if (!hasCollateralField?.name) return;
		    if (!isHasCollateralNo) {
		      setPendingHasCollateralNo(false);
		      return;
		    }
    if (suppressHasCollateralNoModalRef.current) {
      suppressHasCollateralNoModalRef.current = false;
      return;
    }
		    if (fetchedCollateralNumbers.length > 0) {
		      setPendingHasCollateralNo(true);
		    }
	  }, [
	    fetchedCollateralNumbers.length,
	    hasCollateralField?.name,
	    isHasCollateralNo,
	  ]);

  useEffect(() => {
    onCollateralOptionsChange?.({
      addressTypes: addressTypeOptions,
      propertyTypes: propertyTypeOptions,
      propertySubTypes: propertySubTypeOptions,
    });
  }, [
    addressTypeOptions,
    propertyTypeOptions,
    propertySubTypeOptions,
    onCollateralOptionsChange,
  ]);

  // Re-fetch sub-types when selected property type changes
  useEffect(() => {
    if (!selectedPropertyType) return;
    let isMounted = true;
    dropdownService.getPropertySubTypes(selectedPropertyType).then((data) => {
      if (isMounted) setPropertySubTypeOptions(data);
    }).catch(() => {});
    return () => { isMounted = false; };
  }, [selectedPropertyType]);

  // Watch collateral1propertyName to trigger sub-type re-fetch
  useEffect(() => {
    const raw = formValues?.["collateral1propertyName"];
    const val = raw && typeof raw === "object" ? raw?.value : raw;
    if (val && String(val) !== selectedPropertyType) {
      setSelectedPropertyType(String(val));
    }
  }, [formValues?.["collateral1propertyName"]]);

  const getFieldsByGroup = (n: number) => {
    const basePrefix = `collateral${n}`;
    const coOwnerPrefix = `coownercollateral${n}`;

    const base = collateralFields.filter((f: any) => {
      const normalized = getNormalized(f?.name);
      if (normalized.startsWith("coownercollateral")) return false;
      return (
        normalized.startsWith(basePrefix) ||
        (n === 1 && normalized.startsWith("collateral") && !/^collateral\d+/.test(normalized))
      );
    });

    const coOwner = collateralFields.filter((f: any) => {
      const normalized = getNormalized(f?.name);
      return (
        normalized.startsWith(coOwnerPrefix) ||
        (n === 1 && normalized.startsWith("coownercollateral") && !/^coownercollateral\d+/.test(normalized))
      );
    });

    return { base, coOwner };
  };

  const restoreFirstCollateralFields = () => {
    const templateFields = firstCollateralFieldsRef.current;
    if (!templateFields.length) return;

    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;
    const updatedHorizontalSteps = [...(horizontalSteps || [])];
    const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
    const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];
    const targetStep = targetVerticalSteps[currentVerticalIndex];

    if (targetStep) {
      targetVerticalSteps[currentVerticalIndex] = {
        ...targetStep,
        fields: mergeFieldsByName(targetStep.fields || [], templateFields),
      };
      targetHorizontal.verticalSteps = targetVerticalSteps;
      updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
      dispatch(setHorizontalSteps(updatedHorizontalSteps));
    }

    dispatch(
      updateFormData(
        templateFields.reduce((acc: Record<string, any>, field: any) => {
          if (field?.name) {
            acc[field.name] = "";
            formikRef?.current?.setFieldValue(field.name, "", false);
          }
          return acc;
        }, {}),
      ),
    );
  };

  useEffect(() => {
    const switchedFromNoToYes =
      previousHasCollateralValueRef.current === "no" && isHasCollateralYes;
    previousHasCollateralValueRef.current = hasCollateralValue;

    if (!switchedFromNoToYes || removedCollateralNumbers.length === 0) return;

    setRemovedCollateralNumbers([]);
    restoreFirstCollateralFields();
  }, [hasCollateralValue, isHasCollateralYes, removedCollateralNumbers.length]);

  const hasToken = (field: any, tokens: string[]) => {
    const normalizedName = getNormalized(field?.name);
    const normalizedLabel = getNormalized(field?.label);
    return tokens.some(
      (token) =>
        normalizedName.includes(token) || normalizedLabel.includes(token),
    );
  };

  const isCollateralTypeField = (field: any) => {
    const normalized = getNormalized(field?.name);
    return (
      /^collateral\d*type$/.test(normalized) ||
      getNormalized(field?.label).includes("collateraltype")
    );
  };

  const getCollateralMode = (sectionFields: any[]): "fixed" | "savings" | "property" | null => {
    // Match collateralType, collateral1Type, collateral2Type, etc. — by regex on name, or by label substring
    const typeField = sectionFields.find(isCollateralTypeField);

    if (!typeField) return null;

    const raw = formValues?.[typeField?.name];
    const rawValue =
      raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
    const rawLabel =
      raw && typeof raw === "object" && "label" in raw ? raw.label : raw;

    const normalized = getNormalized(rawLabel || rawValue);
    const normalizedValue = String(rawValue ?? "").trim();

    if (
      normalizedValue === "307" ||
      normalized.includes("fixed") ||
      normalized.includes("fd")
    ) {
      return "fixed";
    }

    if (normalizedValue === "309" || normalized.includes("saving")) {
      return "savings";
    }

    if (normalizedValue === "308" || normalized.includes("property")) {
      return "property";
    }

    return null;
  };

  const getVisibleBaseFields = (baseFields: any[]) => {
    const mode = getCollateralMode(baseFields);

    if (!mode) {
      return baseFields.filter(isCollateralTypeField);
    }

    const commonTokens = ["collateraltype","collateralvalue"];

    // Override hasToken for the type field since "collateral1type".includes("collateraltype") = false
    const isTypeOrValueField = (f: any) => {
      const n = getNormalized(f?.name);
      return isCollateralTypeField(f) || /^collateral\d*value$/.test(n) ||
        hasToken(f, commonTokens);
    };
    const fixedTokens = [
      "fdholdername",
      "fdnumber",
    ];
    const savingsTokens = ["accountnumber"];
    const propertyTokens = [
      "unitprice",
      "noofunits",
      "totalvalue",
      "propertypename",
      "propertysubtypename",
      "addresstype",
      "addressline1",
      "addressline2",
      "pincode",
      "city",
      "state",
      "country",
    ];

    const tokenMap: Record<"fixed" | "savings" | "property", string[]> = {
      fixed: fixedTokens,
      savings: savingsTokens,
      property: propertyTokens,
    };

    return baseFields.filter(
      (field: any) =>
        isTypeOrValueField(field) ||
        (mode === "savings" && /^collateral\d+holderName$/i.test(String(field?.name || ""))) ||
        hasToken(field, tokenMap[mode]),
    );
  };

  const getCoOwnerSectionIndex = (fieldName: string, collateralNumber: number) => {
    const normalized = String(fieldName || "");
    const numberedMatch = normalized.match(
      new RegExp(`^coOwnerCollateral${collateralNumber}(?:_(\\d+)|(\\d+))?`, "i"),
    );

    if (numberedMatch) {
      return Number(numberedMatch[1] || numberedMatch[2] || 1);
    }

    return 1;
  };

  const splitCoOwnerSections = (coOwnerFields: any[], collateralNumber: number) => {
    if (!Array.isArray(coOwnerFields) || coOwnerFields.length === 0) return [];

    const sectionMap = new Map<number, any[]>();
    coOwnerFields.forEach((field: any) => {
      const sectionIndex = getCoOwnerSectionIndex(field?.name, collateralNumber);
      const currentSection = sectionMap.get(sectionIndex) || [];
      currentSection.push(field);
      sectionMap.set(sectionIndex, currentSection);
    });

    return Array.from(sectionMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([, sectionFields]) => sectionFields);
  };

  const coOwnerOptionCount = useMemo(() => {
    const currentValues = { ...(formData || {}), ...(formValues || {}) };
    let count = 0;
    const applicantName = [
      currentValues?.firstName || currentValues?.studentDetailsFirstName,
      currentValues?.lastName || currentValues?.studentDetailsLastName,
    ].filter(Boolean).join(" ");
    if (applicantName) count += 1;

    const coApplicantNumbers = new Set<number>();
    Object.keys(currentValues).forEach((key) => {
      const match = key.match(/^basicCoApplicant(\d+)FirstName$/i);
      if (match && String(currentValues[key] ?? "").trim()) {
        coApplicantNumbers.add(Number(match[1]));
      }
    });
    return count + coApplicantNumbers.size;
  }, [formData, formValues]);

  const isFetchedCoOwnerSection = (collateralNumber: number, sectionIdx: number) =>
    [
      ...(fetchedCoOwnerSections[collateralNumber] || []),
      ...(applicationFetchedCoOwnerSections[collateralNumber] || []),
    ].includes(sectionIdx + 1);

  // ── Single-row Ownership Percentage lock ──────────────────────────────────
  // Only one saved co-owner can be updated at a time: once ONE saved
  // co-owner's percentage differs from its saved value, every other
  // co-owner's percentage in that collateral is disabled; reverting it to the
  // saved value unlocks them again.
  const isCoOwnerPercentageField = (name: any) =>
    /^coOwnerCollateral\d+(?:_\d+)?OwnershipPercentage$/i.test(String(name || ""));

  const getSavedCoOwnerPercentage = (fieldName: string): string | undefined =>
    fieldName in fetchedCoOwnerPercentages
      ? fetchedCoOwnerPercentages[fieldName]
      : applicationFetchedCoOwnerPercentages[fieldName];

  const isSavedPercentageEdited = (currentValue: any, savedValue: any) => {
    const current = getSimpleValue(currentValue);
    const saved = getSimpleValue(savedValue);
    if (!saved) return false;
    const currentNum = Number(current);
    const savedNum = Number(saved);
    if (current !== "" && !Number.isNaN(currentNum) && !Number.isNaN(savedNum)) {
      return currentNum !== savedNum;
    }
    return current !== saved;
  };

  const getEditedCoOwnerPercentageFieldName = (sections: any[][]): string | null => {
    const latestValues = formikRef?.current?.values || formValues;
    for (const sectionFields of sections) {
      const percentageField = sectionFields.find((f: any) => isCoOwnerPercentageField(f?.name));
      if (!percentageField?.name) continue;
      const savedValue = getSavedCoOwnerPercentage(percentageField.name);
      if (isSavedPercentageEdited(latestValues?.[percentageField.name], savedValue)) {
        return percentageField.name;
      }
    }
    return null;
  };

  const withCoOwnerPercentageLock = (field: any, editedFieldName: string | null) =>
    editedFieldName &&
    isCoOwnerPercentageField(field?.name) &&
    field.name !== editedFieldName
      ? { ...field, disabled: true }
      : field;

  const renameCoOwnerField = (
    fieldName: string,
    collateralNumber: number,
    ownerIndex: number,
  ) => {
    // Expected format:
    // coOwnerCollateral1Name -> coOwnerCollateral1_2Name
    // coOwnerCollateral1OwnershipPercentage -> coOwnerCollateral1_2OwnershipPercentage
    // coOwnerCollateral1PhoneNumber -> coOwnerCollateral1_2PhoneNumber
    const normalized = String(fieldName || "");
    const match = normalized.match(/^coOwnerCollateral\d+(.*)$/i);

    if (match) {
      const suffix = match[1] || "";
      return ownerIndex > 1
        ? `coOwnerCollateral${collateralNumber}_${ownerIndex}${suffix}`
        : `coOwnerCollateral${collateralNumber}${suffix}`;
    }

    return ownerIndex > 1
      ? `coOwnerCollateral${collateralNumber}_${ownerIndex}${normalized}`
      : `coOwnerCollateral${collateralNumber}${normalized}`;
  };

  const removeCoOwnerSection = async (
    collateralNumber: number,
    sectionFields: any[],
    totalSections: number,
    sectionIdx: number,
  ) => {
    const removeKey = `${collateralNumber}-${sectionIdx}`;
    const fieldNames = new Set(sectionFields.map((f: any) => f?.name));
    const fieldIds = sectionFields
      .map((f: any) => f?.id)
      .filter((id: any) => id !== undefined && id !== null);

    if (!trackingId || fieldIds.length === 0) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Unable to remove Co-Owner fields right now. Please try again.",
        }),
      );
      return;
    }

    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;

    setIsRemovingCoOwner((prev) => ({ ...prev, [removeKey]: true }));

    try {
      const resp = await postsServices.addPost("api/v1/product/delete-user-fields", {
        form_field_ids: fieldIds,
        trackingId,
      });

      if (resp?.data?.status !== "success") {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to remove Co-Owner fields right now. Please try again.",
          }),
        );
        return;
      }

      const updatedHorizontalSteps = [...(horizontalSteps || [])];
      const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
      const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

      if (
        typeof currentVerticalIndex === "number" &&
        currentVerticalIndex >= 0 &&
        currentVerticalIndex < targetVerticalSteps.length
      ) {
        const targetStep = { ...targetVerticalSteps[currentVerticalIndex] };
        targetStep.fields = (targetStep.fields || []).filter(
          (f: any) => !fieldNames.has(f?.name),
        );
        targetVerticalSteps[currentVerticalIndex] = targetStep;
        targetHorizontal.verticalSteps = targetVerticalSteps;
        updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
        dispatch(setHorizontalSteps(updatedHorizontalSteps));
      }

      if (formikRef?.current) {
        sectionFields.forEach((field: any) => {
          formikRef.current.setFieldValue(field.name, undefined, false);
          formikRef.current.setFieldTouched(field.name, false, false);
          formikRef.current.setFieldError(field.name, undefined);
        });
      }
      dispatch(
        updateFormData(
          sectionFields.reduce((acc: Record<string, any>, field: any) => {
            if (field?.name) acc[field.name] = "";
            return acc;
          }, {})
        )
      );

      setVisibleCoOwnerCount((prev) => ({
        ...prev,
        [collateralNumber]: Math.max((prev[collateralNumber] ?? totalSections) - 1, 1),
      }));
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            error?.response?.data?.message ||
            "Unable to remove Co-Owner fields right now. Please try again.",
        }),
      );
    } finally {
      setIsRemovingCoOwner((prev) => ({ ...prev, [removeKey]: false }));
    }
  };

  const handleConfirmRemoveCoOwner = () => {
    if (!pendingCoOwnerRemove) return;
    removeCoOwnerSection(
      pendingCoOwnerRemove.collateralNumber,
      pendingCoOwnerRemove.sectionFields,
      pendingCoOwnerRemove.totalSections,
      pendingCoOwnerRemove.sectionIdx,
    );
    setPendingCoOwnerRemove(null);
  };

	  const handleConfirmRemoveCollateral = () => {
	    if (!pendingCollateralRemove) return;
	    const collateralNumber = pendingCollateralRemove;
	    setPendingCollateralRemove(null);
	    removeCollateralFields(collateralNumber);
	  };

  const handleCloseHasCollateralNoNotice = () => {
    setPendingHasCollateralNo(false);
    setHasCollateralValue("Yes");
  };

  const addCollateralFields = async () => {
    if (isAddingCollateralRef.current) return;
    isAddingCollateralRef.current = true;
    setIsAddingCollateral(true);
    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;
    const verticalStepId =
      horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[currentVerticalIndex]?.id;

    if (!verticalStepId || !trackingId) {
      dispatch(setStatusPopup({ status: "error", message: "Unable to add Collateral fields right now. Please try again." }));
      isAddingCollateralRef.current = false;
      setIsAddingCollateral(false);
      return;
    }

    // Use collateral 1 base fields + co-owner fields as the template, renaming to collateral N
    const collateral1Group = getFieldsByGroup(1);
    const firstCoOwnerSection = splitCoOwnerSections(collateral1Group.coOwner, 1)[0] || [];
    const collateral1Fields = [...collateral1Group.base, ...firstCoOwnerSection];
    if (collateral1Fields.length === 0) {
      dispatch(setStatusPopup({ status: "error", message: "No template fields found for Collateral 1." }));
      isAddingCollateralRef.current = false;
      setIsAddingCollateral(false);
      return;
    }

	    let nextNum = displayedCollateralNumbers.length > 0
	      ? Math.max(...displayedCollateralNumbers) + 1
	      : 1;
	
	        const res = await eligibilityCheckServices.fetchCollateral(trackingId).catch(() => null);
	        if(res && res?.status === 200) {
          const fetchedNumbers = collectFetchedCollateralNumbers(res?.data?.data);
          const knownNumbers = [...displayedCollateralNumbers, ...fetchedNumbers];
          nextNum = knownNumbers.length > 0 ? Math.max(...knownNumbers) + 1 : 1;
	        }
	
	    const renameToNext = (name: string, collateralNumber: number): string => {
	      // coOwnerCollateral1XYZ -> coOwnerCollateral2XYZ
	      if (/^coOwnerCollateral/i.test(name)) {
	        return name.replace(/^(coOwnerCollateral)(\d+)?/i, `$1${collateralNumber}`);
	      }
	      // collateral1XYZ -> collateral2XYZ, collateralXYZ -> collateral2XYZ
	      return name.replace(/^(collateral)(\d+)?/i, `$1${collateralNumber}`);
	    };

    const apiFields = collateral1Fields.map((field: any) => ({
      label: field?.label,
	      name: renameToNext(String(field?.name || ""), nextNum),
      type: field?.type,
      required: field?.required,
      options: field?.options ?? null,
    }));

    try {
      const resp = await postsServices.addPost("api/v1/product/store-user-fields", {
        vertical_step_id: verticalStepId,
        trackingId,
        fields: apiFields,
      });

      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ?? null,
        }));

        const updatedHorizontalSteps = [...(horizontalSteps || [])];
        const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
        const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

        if (
          typeof currentVerticalIndex === "number" &&
          currentVerticalIndex >= 0 &&
          currentVerticalIndex < targetVerticalSteps.length
        ) {
          const targetStep = { ...targetVerticalSteps[currentVerticalIndex] };
          targetStep.fields = mergeFieldsByName(targetStep.fields || [], newFields);
          targetVerticalSteps[currentVerticalIndex] = targetStep;
          targetHorizontal.verticalSteps = targetVerticalSteps;
          updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
          dispatch(setHorizontalSteps(updatedHorizontalSteps));
        }

        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            formikRef.current.setFieldValue(field.name, "", false);
          });
        }

        setLocallyAddedCollateralNumbers((prev) =>
          prev.includes(nextNum) ? prev : [...prev, nextNum]
        );
        setRemovedCollateralNumbers((prev) =>
          prev.filter((number) => number !== nextNum)
        );
        setExpanded(nextNum);
        onActiveCollateralChange?.(nextNum);
      } else {
        dispatch(setStatusPopup({ status: "error", message: "Unable to add Collateral fields right now. Please try again." }));
      }
    } catch (error: any) {
      dispatch(setStatusPopup({ status: "error", message: error?.response?.data?.message || "Unable to add Collateral fields right now. Please try again." }));
    } finally {
      isAddingCollateralRef.current = false;
      setIsAddingCollateral(false);
    }
  };

  const removeCollateralFields = async (n: number) => {
    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;

    const group = getFieldsByGroup(n);
    const allCollateralFields = [...group.base, ...group.coOwner];
    const firstCoOwnerFields = splitCoOwnerSections(group.coOwner, n)[0] || [];
    const firstCoOwnerNames = new Set(firstCoOwnerFields.map((field: any) => field?.name));
    const additionalCoOwnerFieldIds = group.coOwner
      .filter((field: any) => !firstCoOwnerNames.has(field?.name))
      .map((field: any) => field?.id)
      .filter((id: any) => id !== undefined && id !== null);
    if (n === 1 && firstCollateralFieldsRef.current.length === 0) {
      firstCollateralFieldsRef.current = [...group.base, ...firstCoOwnerFields];
    }
    const fieldIds = allCollateralFields
      .map((f: any) => f?.id)
      .filter((id: any) => id !== undefined && id !== null);
    const fieldNames = new Set(allCollateralFields.map((f: any) => f?.name));

	    if (!trackingId || (n !== 1 && fieldIds.length === 0)) {
	      dispatch(setStatusPopup({ status: "error", message: "Unable to remove Collateral fields right now. Please try again." }));
	      return;
	    }
	
	    setIsRemovingCollateral((prev) => ({ ...prev, [n]: true }));
	    try {
      const hasPersistedCollateral =
        fetchedCollateralNumbers.includes(n);

      const userFieldIds = n === 1 ? additionalCoOwnerFieldIds : fieldIds;
      if (hasPersistedCollateral && userFieldIds.length > 0) {
	      const resp = await postsServices.addPost("api/v1/product/delete-user-fields", {
	        form_field_ids: userFieldIds,
	        trackingId,
	      });
	
	      if (resp?.data?.status !== "success") {
	        dispatch(setStatusPopup({ status: "error", message: "Unable to remove Collateral fields right now. Please try again." }));
	        return;
	      }
      }
	
	      const collateralSequenceType = `COLLATERAL_${numberToWord[n] || n}`;

	      if (hasPersistedCollateral) {
	        const deleteCollateralResp = await eligibilityCheckServices.deleteCollateral({
	          trackingId,
	          collateralSequenceType,
          eventName: "DELETE_COLLATERAL",
        });

        if (
          deleteCollateralResp?.data?.status &&
          String(deleteCollateralResp.data.status).toLowerCase() === "fail"
        ) {
          dispatch(setStatusPopup({ status: "error", message: "Unable to remove Collateral fields right now. Please try again." }));
          return;
        }
      }

	    const updatedHorizontalSteps = [...(horizontalSteps || [])];
	      const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
	      const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];
	
	      if (
	        typeof currentVerticalIndex === "number" &&
	        currentVerticalIndex >= 0 &&
	        currentVerticalIndex < targetVerticalSteps.length
	      ) {
	        const targetStep = { ...targetVerticalSteps[currentVerticalIndex] };
	        targetStep.fields = (targetStep.fields || []).filter((f: any) => !fieldNames.has(f?.name));
	        targetVerticalSteps[currentVerticalIndex] = targetStep;
	        targetHorizontal.verticalSteps = targetVerticalSteps;
	        updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
	        dispatch(setHorizontalSteps(updatedHorizontalSteps));
	      }

      if (formikRef?.current) {
        allCollateralFields.forEach((field: any) => {
          formikRef.current.setFieldValue(field.name, undefined, false);
        });
      }
      dispatch(
        updateFormData(
          allCollateralFields.reduce((acc: Record<string, any>, field: any) => {
            if (field?.name) acc[field.name] = "";
            return acc;
          }, {}),
        ),
      );
      setLocallyAddedCollateralNumbers((prev) =>
        prev.filter((number) => number !== n)
      );

      setRemovedCollateralNumbers((prev) =>
        prev.includes(n) ? prev : [...prev, n]
      );
      setFetchedCollateralNumbers((prev) => prev.filter((number) => number !== n));
      setFetchedCollateralTypeMap((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      });

	      const remainingCollateralNumbers = displayedCollateralNumbers.filter(
        (num) => num !== n,
      );
	      if (remainingCollateralNumbers.length === 0) {
	        setFetchedCollateralNumbers([]);
	        setFetchedCollateralTypeMap({});
          setLocallyAddedCollateralNumbers([]);
	        setHasCollateralValue("No", true);
	      }
	      const nextActiveCollateral = remainingCollateralNumbers[0];
      setExpanded(nextActiveCollateral ?? false);
      if (nextActiveCollateral !== undefined) {
        onActiveCollateralChange?.(nextActiveCollateral);
      }
    } catch (error: any) {
      dispatch(setStatusPopup({ status: "error", message: error?.response?.data?.message || "Unable to remove Collateral fields right now. Please try again." }));
    } finally {
      setIsRemovingCollateral((prev) => ({ ...prev, [n]: false }));
    }
  };

  const addCoOwnerFields = async (collateralNumber: number, templateSection: any[]) => {
    if (!Array.isArray(templateSection) || templateSection.length === 0) {
      return;
    }
    const currentVerticalIndex =
      typeof activeVerticalIndex === "object"
        ? activeVerticalIndex?.[activeHorizontalIndex] ?? 0
        : 0;
    const verticalStepId =
      horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[currentVerticalIndex]?.id;

    if (!verticalStepId || !trackingId) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Unable to add Co-Owner fields right now. Please try again.",
        }),
      );
      return;
    }

    const groupedForCollateral = getFieldsByGroup(collateralNumber);
    const ownerSections = splitCoOwnerSections(groupedForCollateral.coOwner, collateralNumber);
    const nextOwnerIndex = Math.max(ownerSections.length, 0) + 1;

    const apiFields = templateSection.map((field: any) => {
      const originalName = String(field?.name || "");

      // check if name ends with only 1
      const shouldRename = /(collateral|coowner)1(?!\d)/.test(originalName);
      return {
        label: field?.label,
        name: renameCoOwnerField(
              originalName,
              collateralNumber,
              nextOwnerIndex,
            ),
        type: field?.type,
        required: field?.required,
        options: field?.options ?? null,
      };
    });

    setIsAddingCoOwner((prev) => ({ ...prev, [collateralNumber]: true }));

    try {
      const resp = await postsServices.addPost("api/v1/product/store-user-fields", {
        vertical_step_id: verticalStepId,
        trackingId,
        fields: apiFields,
      });

      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        const updatedHorizontalSteps = [...(horizontalSteps || [])];
        const targetHorizontal = { ...(updatedHorizontalSteps?.[activeHorizontalIndex] || {}) };
        const targetVerticalSteps = [...(targetHorizontal?.verticalSteps || [])];

        if (
          typeof currentVerticalIndex === "number" &&
          currentVerticalIndex >= 0 &&
          currentVerticalIndex < targetVerticalSteps.length
        ) {
          const targetStep = { ...targetVerticalSteps[currentVerticalIndex] };
          targetStep.fields = mergeFieldsByName(targetStep.fields || [], newFields);
          targetVerticalSteps[currentVerticalIndex] = targetStep;
          targetHorizontal.verticalSteps = targetVerticalSteps;
          updatedHorizontalSteps[activeHorizontalIndex] = targetHorizontal;
          dispatch(setHorizontalSteps(updatedHorizontalSteps));
        }

        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            const defaultValue = field?.type === "boolean" ? false : "";
            formikRef.current.setFieldValue(field.name, defaultValue, false);
          });
        }

        setVisibleCoOwnerCount((prev) => ({
          ...prev,
          [collateralNumber]: (prev[collateralNumber] ?? ownerSections.length) + 1,
        }));
      } else {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to add Co-Owner fields right now. Please try again.",
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            error?.response?.data?.message ||
            "Unable to add Co-Owner fields right now. Please try again.",
        }),
      );
    } finally {
      setIsAddingCoOwner((prev) => ({ ...prev, [collateralNumber]: false }));
    }
  };

  const enrichCollateralField = (field: any): any => {
      const normalized = getNormalized(field?.name);
      // Always read the latest Formik values to avoid stale closure issues
      const latestValues = formikRef?.current?.values || formValues;
	      const withFetchedTypeLock = (nextField: any) => {
	        const typeMatch = normalized.match(/^collateral(\d*)type$/);
	        if (!typeMatch) return nextField;
	        const collateralNumber = Number(typeMatch[1] || 1);
	        return fetchedCollateralTypeMap[collateralNumber]
	          ? { ...nextField, disabled: true }
	          : nextField;
	      };
      const withCollateralRequiredFlag = (nextField: any) => {
        const collateralMatch = normalized.match(/^collateral(\d*)/);
        const coOwnerMatch = normalized.match(/^coownercollateral(\d+)/);
        const collateralNumber = Number(collateralMatch?.[1] || coOwnerMatch?.[1] || 1);
        const mode = getCollateralMode(getFieldsByGroup(collateralNumber).base);
        const propertyRequiredTokens = [
          "unitprice",
          "noofunits",
          "totalvalue",
          "propertypename",
          "propertysubtypename",
          "addresstype",
          "addressline1",
          "pincode",
          "city",
          "state",
          "country",
        ];
        const fixedRequiredTokens = ["fdholdername", "fdnumber"];
        const savingsRequiredTokens = ["holdername", "accountnumber"];
        const isRequired =
          isCollateralTypeField(nextField) ||
          /^collateral\d*value$/.test(normalized) ||
          (mode === "property" && propertyRequiredTokens.some((token) => normalized.includes(token))) ||
          (mode === "fixed" && fixedRequiredTokens.some((token) => normalized.includes(token))) ||
          (mode === "savings" && savingsRequiredTokens.some((token) => normalized.includes(token))) ||
          (/^coownercollateral/.test(normalized) &&
            /(name|ownershippercentage|phonenumber)$/.test(normalized));

        return isRequired ? { ...nextField, required: true } : nextField;
      };
      const finalizeField = (nextField: any) =>
        withFetchedTypeLock(
          withCollateralRequiredFlag(
            /^collateral\d*(city|state|country)$/.test(normalized) ||
            /^coOwnerCollateral\d+(?:_\d+)?PhoneNumber$/i.test(String(field?.name || ""))
              ? { ...nextField, disabled: true }
              : nextField
          )
        );

      if (isCollateralTypeField(field)) {
        return finalizeField(field);
      }
	
	      // Exclude selected propertyType in Collateral N from Collateral N+1 dropdown
	      if (normalized.includes("propertysubtypename")) {
        return finalizeField({ ...field, options: { options: propertySubTypeOptions } });
      }
      if (normalized.includes("propertypename") && !normalized.includes("subtype")) {
        const sectionMatch = normalized.match(/^collateral(\d+)/);
        const sectionNum = sectionMatch ? sectionMatch[1] : null;
        let filteredOptions = propertyTypeOptions;
        if (sectionNum && sectionNum !== "1") {
          const collateral1PropertyField = collateralFields.find((f: any) => {
            const n = getNormalized(f?.name);
            return (
              n.includes("propertypename") &&
              !n.includes("subtype") &&
              !n.startsWith("coownercollateral") &&
              (n.startsWith("collateral1") || !n.match(/^collateral\d+/))
            );
          });
          const raw = collateral1PropertyField ? latestValues?.[collateral1PropertyField.name] : undefined;
          const selectedValue = raw && typeof raw === "object" ? raw.value : raw;
          if (selectedValue !== undefined && selectedValue !== null && String(selectedValue).trim() !== "") {
            filteredOptions = propertyTypeOptions.filter(
              (opt) => String(opt.value) !== String(selectedValue),
            );
          }
        }
        return finalizeField({ ...field, options: { options: filteredOptions } });
      }
      if (normalized.endsWith("addresstype")) {
        return finalizeField({ ...field, options: { options: addressTypeOptions } });
      }
      return finalizeField(field);
  };

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const required = sectionFields.filter((f: any) => f?.required !== false);
    const filled = required.filter((f: any) =>
      isFilled(formValues?.[f?.name]),
    ).length;
    return `${filled} / ${required.length}`;
  };

  const fixedColumnGridSx = {
    // Force a consistent 3-column layout on >= sm for fields rendered by parent renderField.
    "@media (min-width:600px)": {
      "& > .MuiGrid-item": {
        flexBasis: "33.333333%",
        maxWidth: "33.333333%",
      },
    },
  };

  const baseGridSx = {
    ...fixedColumnGridSx,
    "@media (min-width:600px)": {
      ...fixedColumnGridSx["@media (min-width:600px)"],
      // Keep first field on a separate row, but preserve same visual control width.
      "& > .MuiGrid-item:first-of-type": {
        flexBasis: "100%",
        maxWidth: "100%",
      },
      "& > .MuiGrid-item:first-of-type > .MuiBox-root": {
        width: "33.333333%",
      },
    },
  };

  // "Add More" is enabled only when all required fields of the last collateral section are filled
	  const lastCollateralNum = displayedCollateralNumbers[displayedCollateralNumbers.length - 1];
  const shouldShowCollateralBody = !isHasCollateralNo && displayedCollateralNumbers.length >= 0;
    console.log(shouldShowCollateralBody, displayedCollateralNumbers.length , !isHasCollateralNo, 'shouldShowCollateralBody')
    console.log(displayedCollateralNumbers, 'displayedCollateralNumbers')
	  const isLastCollateralComplete = useMemo(() => {
    if (!lastCollateralNum) return false;
    const { base, coOwner } = getFieldsByGroup(lastCollateralNum);
	    const visibleBase = getVisibleBaseFields(base);
	    const coOwnerSections = splitCoOwnerSections(coOwner, lastCollateralNum);
	    const visibleCoOwner = coOwnerSections.flat();
	    const allSectionFields = [...visibleBase, ...visibleCoOwner].map(enrichCollateralField);
	    const requiredFields = allSectionFields.filter((f: any) => f?.required !== false);
    if (requiredFields.length === 0) return true;
    return requiredFields.every((f: any) => isFilled(formValues?.[f?.name]));
  }, [lastCollateralNum, collateralFields, formValues]);
	  return (
	    <Box sx={{ width: "100%", mb: 2 }}>
      {isCollateralFetchLoading && (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={38} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={64} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={160} />
        </Box>
      )}

      {hasCollateralField && !isCollateralFetchLoading && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {renderField(hasCollateralField)}
        </Grid>
      )}
        {shouldShowCollateralBody }
      
	      {shouldShowCollateralBody && displayedCollateralNumbers.length < 5 && (
	        <Box
	          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 1,
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: "13px",
              color: isLastCollateralComplete ? "#00B3B5" : "#b0b0b0",
              display: "flex",
              alignItems: "center",
              cursor: isLastCollateralComplete ? "pointer" : "not-allowed",
              pointerEvents: isLastCollateralComplete ? "auto" : "none",
              userSelect: "none",
            }}
            onClick={isLastCollateralComplete ? addCollateralFields : undefined}
          >
            {isAddingCollateral ? (
              "Adding..."
            ) : (
              <>
                <AddIcon /> Add More
              </>
            )}
          </Typography>
        </Box>
      )}

	      {shouldShowCollateralBody && displayedCollateralNumbers.map((n, i) => {
        const grouped = getFieldsByGroup(n);
        const collateralMode = getCollateralMode(grouped.base);
        const visibleBaseFields = getVisibleBaseFields(grouped.base);
        const coOwnerSections = collateralMode ? splitCoOwnerSections(grouped.coOwner, n) : [];
        const currentVisibleCoOwnerCount = Math.min(
          Math.max(visibleCoOwnerCount[n] ?? coOwnerSections.length, 1),
          Math.max(coOwnerSections.length, 1),
        );
        const visibleCoOwnerSections = coOwnerSections.slice(0, currentVisibleCoOwnerCount);
        const visibleCoOwnerFields = visibleCoOwnerSections.flat();
        const allFields = [...visibleBaseFields, ...visibleCoOwnerFields];
        const editedCoOwnerPercentageFieldName =
          getEditedCoOwnerPercentageFieldName(coOwnerSections);
        // Update flow: at least one co-owner of this collateral is already saved.
        const hasSavedCoOwner = coOwnerSections.some((_: any[], idx: number) =>
          isFetchedCoOwnerSection(n, idx),
        );

        return (
          <Box key={n} sx={{ mb: 2 }}>
            <Accordion
              expanded={expanded === n}
              onChange={() => {
              const nextExpanded = expanded === n ? false : n;
              setExpanded(nextExpanded);
              if (nextExpanded !== false) {
                onActiveCollateralChange?.(nextExpanded);
              }
            }}
              sx={{
                border: "1px solid #d6eef1",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                backgroundColor: "#fff",
                overflow: "hidden",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 0 16px" },
              }}
            >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: "64px",
                px: { xs: 2, sm: 3 },
                backgroundColor: "#eaf7f9",
                borderBottom: expanded === n ? "1px solid #d6eef1" : "none",
                "& .MuiAccordionSummary-content": {
                  my: 1.5,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                <Typography fontWeight={600} fontSize="15px">
                  Collateral {i+ 1}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
              <Grid container spacing={2} sx={baseGridSx}>
                {visibleBaseFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(enrichCollateralField(field))}
                  </React.Fragment>
                ))}
              </Grid>

              {grouped.coOwner.length > 0 && (
                <>
                  <Box
                    sx={{
                      mt: 2.5,
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 700, color: "#6f6b7d" }}
                    >
                      Co-Owner Information
                    </Typography>
                    <Button
                      sx={{
                        textTransform: "none",
                        fontFamily: "Jost, sans-serif",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#33c2c4",
                        minWidth: "auto",
                        p: 0,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                      onClick={() => {
                        addCoOwnerFields(n, coOwnerSections?.[0] || []);
                      }}
                      disabled={
                        Boolean(isAddingCoOwner[n]) ||
                        (coOwnerOptionCount > 0 &&
                          visibleCoOwnerSections.length >= coOwnerOptionCount)
                      }
                    >
                      <AddIcon sx={{ mr: 0.4, fontSize: "16px" }} />
                      {isAddingCoOwner[n] ? "Adding..." : "Add More"}
                    </Button>
                  </Box>

                  {hasSavedCoOwner && (
                    <Box
                      sx={{
                        mb: 2,
                        px: 1.5,
                        py: 1,
                        backgroundColor: "#FFF4CC",
                        border: " #FFF4CC",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", color: "#7A5A00" }}>
                        You can only update one co-owner at a time.
                      </Typography>
                    </Box>
                  )}

                  {visibleCoOwnerSections.map((sectionFields: any[], sectionIdx: number) => (
                    <Box key={`co-owner-section-${n}-${sectionIdx}`} sx={{ mb: sectionIdx === visibleCoOwnerSections.length - 1 ? 0 : 2 }}>
                      <Typography sx={{ fontSize: "13px", color: "#6f6b7d", mb: 1.5 }}>
                        Co-Owner {sectionIdx + 1}
                      </Typography>

                      <Grid container spacing={2} sx={fixedColumnGridSx}>
                        {sectionFields.map((field: any) => (
	                          <React.Fragment key={field?.id || field?.name}>
	                            {renderField(withCoOwnerPercentageLock(enrichCollateralField(field), editedCoOwnerPercentageFieldName))}
	                          </React.Fragment>
	                        ))}
                      </Grid>

                      {sectionIdx > 0 && !isFetchedCoOwnerSection(n, sectionIdx) && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                          <Button
                            sx={{
                              textTransform: "none",
                              fontFamily: "Jost, sans-serif",
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#ea5455",
                              minWidth: "auto",
                              p: 0,
                              "&:hover": { backgroundColor: "transparent" },
                            }}
                            onClick={() =>
                              setPendingCoOwnerRemove({
                                collateralNumber: n,
                                sectionFields,
                                totalSections: coOwnerSections.length,
                                sectionIdx,
                              })
                            }
                            disabled={Boolean(isRemovingCoOwner[`${n}-${sectionIdx}`])}
                          >
                            <RemoveCircleOutlineIcon sx={{ mr: 0.4, fontSize: "16px" }} />
                            {isRemovingCoOwner[`${n}-${sectionIdx}`] ? "Removing..." : "Remove"}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}

              <Typography
                sx={{
                  mt: 1.5,
                  color: "#6f6b7d",
                  fontSize: "12px",
                  textAlign: "right",
                }}
              >
                Field Completion {getCompletion(allFields)}
              </Typography>
            </AccordionDetails>
            </Accordion>

	            {(
	              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
	                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontFamily: "Jost, sans-serif",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#ea5455",
                    minWidth: "auto",
                    p: 0,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                  onClick={() => {
                    setPendingCollateralRemove(n);
                  }}
                  disabled={Boolean(isRemovingCollateral[n])}
                >
                  <RemoveCircleOutlineIcon sx={{ mr: 0.4, fontSize: "15px" }} />
                  {isRemovingCollateral[n] ? "Removing..." : "Remove"}
	                </Button>
	              </Box>
	            )}
          </Box>
        );
      })}

	      {shouldShowCollateralBody && collateralFields.length === 0 && (
	        <Typography color="text.secondary" sx={{ p: 2 }}>
	          No collateral fields available.
	        </Typography>
	      )}

      <ModalPopup
        isOpen={Boolean(pendingCoOwnerRemove)}
        onClose={() => setPendingCoOwnerRemove(null)}
        subTitle="Remove Co-Owner"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this Co-Owner? This action cannot be
            undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
            <Button
              onClick={() => setPendingCoOwnerRemove(null)}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemoveCoOwner}
              color="error"
              size="small"
              variant="contained"
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </ModalPopup>
      <ModalPopup
        isOpen={pendingCollateralRemove !== null}
        onClose={() => setPendingCollateralRemove(null)}
        subTitle="Remove Collateral"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this Collateral? This action cannot
            be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
            <Button
              onClick={() => setPendingCollateralRemove(null)}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton
                onClick={handleConfirmRemoveCollateral}
                variant="contained"
              >
                Confirm
              </PrimaryButton>
          </Box>
        </Box>
	      </ModalPopup>
	      <ModalPopup
	        isOpen={pendingHasCollateralNo}
	        onClose={handleCloseHasCollateralNoNotice}
	        subTitle="Collateral Details"
	      >
	        <Box sx={{ p: 2 }}>
	          <Typography>
	            To select No for Has Collateral, please remove all existing collateral records manually first.
	          </Typography>
	          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
	            <PrimaryButton onClick={handleCloseHasCollateralNoNotice} variant="contained">
	              OK
	            </PrimaryButton>
	          </Box>
	        </Box>
      </ModalPopup>
	    </Box>
	  );
};

export default CollateralDetails;
